import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { readTabState, writeTabState } from '../utils/tabStorage';

/**
 * MI-139 — state for the Movements "Display" menu.
 *
 * Deliberately separate from FilterContext: Display decides how the set is
 * *presented*, Filter decides *which* movements are in it. They are two
 * buttons and two popovers, exactly as in Linear, and folding one into the
 * other would entangle unrelated state.
 *
 * State is client-side only — persisting it server-side is out of scope for
 * MI-139 (see the design spec).
 */

export const VIEW_LIST = 'list';
export const VIEW_BOARD = 'board';

export const GROUPING_NONE = 'none';
export const GROUPING_CATEGORY = 'category';
export const GROUPING_ACCOUNT = 'account';
export const GROUPING_TYPE = 'type';
export const GROUPING_DAY = 'day';
export const GROUPING_MONTH = 'month';
export const GROUPING_YEAR = 'year';
export const GROUPING_GOAL = 'goal';

export const GROUPINGS = [
  GROUPING_CATEGORY,
  GROUPING_ACCOUNT,
  GROUPING_GOAL,
  GROUPING_TYPE,
  GROUPING_DAY,
  GROUPING_MONTH,
  GROUPING_YEAR,
  GROUPING_NONE,
];

export const ORDER_AMOUNT_DESC = 'amount_desc';
export const ORDER_AMOUNT_ASC = 'amount_asc';
export const ORDER_DATE_DESC = 'date_desc';
export const ORDER_DATE_ASC = 'date_asc';
export const ORDER_NAME_ASC = 'name_asc';

export const ORDERINGS = [
  ORDER_AMOUNT_DESC,
  ORDER_AMOUNT_ASC,
  ORDER_DATE_DESC,
  ORDER_DATE_ASC,
  ORDER_NAME_ASC,
];

export const ROWS_PER_GROUP_OPTIONS = [5, 10, 25];

/** 10 reads as a useful sample of a group; 5 felt like a teaser. */
export const DEFAULT_ROWS_PER_GROUP = 10;

/**
 * Row columns the user can hide. Amount and name are the row's identity and
 * are never toggleable.
 *
 * `date` is the movement's own date (MI-336). It left this list in MI-139
 * rev 4, on the argument that the day header above the row already carried
 * it — true of every row under a header, and false the moment "no grouping"
 * became a list with no headers at all. It is honoured literally: on means on,
 * day header or not.
 *
 * `created` is a different fact — when the row entered Mi Kapital, not when
 * the money moved. An imported statement is full of movements dated months
 * before the day they arrived, and nothing on screen said so.
 */
export const DISPLAY_PROPERTIES = [
  'account', 'currency', 'category', 'date', 'reconciled', 'created', 'goal',
];

/** Off unless asked for: another date on every row is noise for the reader who
 *  did not come looking for it.
 *
 *  The goal mark is on by default. It only draws on rows that belong to a
 *  goal, so it costs nothing on the rest — and off by default, it was a way of
 *  seeing goals in the list that nobody found. */
export const PROPERTIES_HIDDEN_BY_DEFAULT = ['created', 'date'];

/**
 * Whether the movement's own date is worth a column, given the grouping.
 *
 * Under a date grouping the header above the row already says the date, so a
 * column repeating it on every row is noise — and Day is the default view, so
 * that is the state the reader arrives in. Ungrouped there is no header at
 * all, which is the case the property was brought back for (MI-336).
 *
 * This is a *default*, not a rule: the chip stays in the menu under every
 * grouping and an explicit toggle is obeyed until the grouping changes again.
 */
export function dateIsRedundantUnder(grouping) {
  return isDateGrouping(grouping);
}

/**
 * Same rule for the goal mark: under a goal grouping the header above the row
 * already names the goal, so the mark repeats it. A row that no longer counts
 * keeps its mark regardless — that is what the header cannot say.
 */
export function goalIsRedundantUnder(grouping) {
  return grouping === GROUPING_GOAL;
}

export const GROUPING_CURRENCY = 'currency';

/** A subgroup may be any grouping except the one the parent already uses. */
export const SUBGROUPINGS = [
  GROUPING_CATEGORY,
  GROUPING_ACCOUNT,
  GROUPING_GOAL,
  GROUPING_TYPE,
  GROUPING_CURRENCY,
  GROUPING_DAY,
  GROUPING_MONTH,
  GROUPING_YEAR,
  GROUPING_NONE,
];

/** Groupings keyed by a date bucket — these have no name to sort on.
 *  Ordered coarse → fine, which is also the nesting order. */
const DATE_GROUPINGS = [GROUPING_YEAR, GROUPING_MONTH, GROUPING_DAY];

/**
 * Which subgroups make sense under a given grouping.
 *
 * Date dimensions nest: a day belongs to exactly one month, so subgrouping a
 * day by month yields one child holding everything — a level that costs a
 * click and says nothing. A date subgroup therefore has to be *finer* than the
 * date it sits under; Day, being finest, admits none at all. Non-date
 * dimensions are unaffected: any category/account/type/currency split of a
 * date bucket is meaningful.
 */
export function subgroupingsFor(grouping) {
  const groupDepth = DATE_GROUPINGS.indexOf(grouping);
  return SUBGROUPINGS.filter(sub => {
    if (sub === grouping) return false;
    const subDepth = DATE_GROUPINGS.indexOf(sub);
    if (groupDepth === -1 || subDepth === -1) return true;
    return subDepth > groupDepth;
  });
}

/**
 * Backend dimensions for a grouping. `month` must travel with `year`:
 * extract('month') alone yields 1..12 and would merge June 2025 into June 2026.
 */
export function dimensionsFor(grouping) {
  switch (grouping) {
    case GROUPING_CATEGORY: return ['currency', 'category_id'];
    case GROUPING_ACCOUNT:  return ['currency', 'account_id'];
    case GROUPING_GOAL:     return ['currency', 'goal_id'];
    case GROUPING_TYPE:     return ['currency', 'type'];
    case GROUPING_DAY:      return ['currency', 'period'];
    case GROUPING_MONTH:    return ['currency', 'year', 'month'];
    case GROUPING_YEAR:     return ['currency', 'year'];
    case GROUPING_CURRENCY: return ['currency'];
    default:                return null;
  }
}

/**
 * Dimensions for a two-level request. `currency` is always present — the
 * backend requires it and it is what lets a merged group know whether it
 * holds one currency or several.
 */
export function dimensionsForLevels(grouping, subgrouping) {
  const base = dimensionsFor(grouping);
  if (!base) return null;
  const sub = dimensionsFor(subgrouping);
  if (!sub) return base;
  return [...new Set([...base, ...sub])];
}

/** The single dimension the row-window endpoint scopes on. */
export function rowDimensionFor(grouping) {
  switch (grouping) {
    case GROUPING_CATEGORY: return 'category_id';
    case GROUPING_ACCOUNT:  return 'account_id';
    case GROUPING_GOAL:     return 'goal_id';
    case GROUPING_TYPE:     return 'type';
    case GROUPING_DAY:      return 'period';
    case GROUPING_MONTH:    return 'month';
    case GROUPING_YEAR:     return 'year';
    case GROUPING_CURRENCY: return 'currency';
    default:                return null;
  }
}

export function granularityFor(grouping) {
  return grouping === GROUPING_DAY ? 'day' : null;
}

export function isDateGrouping(grouping) {
  return DATE_GROUPINGS.includes(grouping);
}

/**
 * Whether a dimension can report *absence* — the values it could have shown
 * and did not.
 *
 * Every dimension except day: a category, an account, a type, a currency, a
 * month or a year is a thing that exists whether or not the period put
 * anything in it, and saying "nothing here" about it is a finding. A day is
 * not — most days are empty, and listing them buries the ones that are not.
 */
export function canHaveEmptyGroups(dimension) {
  return Boolean(dimension) && dimension !== GROUPING_NONE && dimension !== GROUPING_DAY;
}

/**
 * MI-326 — Day is not on offer on the board, as a group or as a subgroup.
 *
 * A column per day turns a month into thirty columns and a year into a
 * sideways scroll nobody reads; a lane per day is the same thing on its side.
 * The list already draws days one under the other, which is the shape they
 * want.
 */
export function offeredIn(view, dimension) {
  return view !== VIEW_BOARD || dimension !== GROUPING_DAY;
}

/** Where a day grouping goes when the board takes it away. Month would be the
 *  obvious neighbour, but over the default one-month range it draws a single
 *  column; a board by category is the one worth opening. */
const BOARD_GROUPING = GROUPING_CATEGORY;

/**
 * The date column and the goal mark follow the grouping: off where a header
 * already carries them, on where nothing else does. A later explicit toggle
 * wins until the grouping changes again.
 */
function propertiesFor(grouping, hidden) {
  const withoutDate = hidden.filter(p => p !== 'date');
  const withDate = dateIsRedundantUnder(grouping) ? [...withoutDate, 'date'] : withoutDate;
  const withoutGoal = withDate.filter(p => p !== 'goal');
  return goalIsRedundantUnder(grouping) ? [...withoutGoal, 'goal'] : withoutGoal;
}

/** A board grouped by day, from a link or from storage written before MI-326,
 *  arrives the way switching to the board would have left it. */
function fitToView(state) {
  if (offeredIn(state.view, state.grouping)) return state;
  return {
    ...state,
    grouping: BOARD_GROUPING,
    hiddenProperties: propertiesFor(BOARD_GROUPING, state.hiddenProperties),
  };
}

/**
 * True when currency is one of the two levels, so every leaf group — and
 * therefore every row window — holds exactly one currency.
 */
export function splitsByCurrency(grouping, subgrouping) {
  return grouping === GROUPING_CURRENCY || subgrouping === GROUPING_CURRENCY;
}

/**
 * Which orderings are unavailable right now, and why.
 *
 * Both gates exist for the same reason — the comparison would be meaningless:
 *  - amount, across more than one currency: COP and USD differ by orders of
 *    magnitude, so a global sort by magnitude ranks by unit size, not value.
 *  - name, on a date bucket: a day/month/year has no name to alphabetise.
 *
 * The gate is on the *data*, never on whether the user happened to click a
 * filter — a workspace that only uses COP must not be asked for a redundant
 * filter to unlock sorting.
 */
export function disabledOrderings({
  currencyCount, grouping, subgrouping, converting = false,
}) {
  const disabled = {};
  // MI-318: converting collapses every currency into one unit, so ranking by
  // magnitude compares value again instead of unit size — the gate lifts.
  // MI-139 rev 5: so does splitting by currency. What has to hold one currency
  // is the scope the ordering runs in, not the whole set — and a currency
  // group's row window is fetched with `currency` in its scope, so its rows
  // are one currency by construction.
  if (currencyCount > 1 && !converting && !splitsByCurrency(grouping, subgrouping)) {
    disabled[ORDER_AMOUNT_DESC] = 'multiCurrency';
    disabled[ORDER_AMOUNT_ASC] = 'multiCurrency';
  }
  if (isDateGrouping(grouping)) {
    disabled[ORDER_NAME_ASC] = 'dateGrouping';
  }
  return disabled;
}

/**
 * Which ordering the *group* level should use.
 *
 * Ordering months by amount interleaves them into an unreadable sequence, so
 * date groupings always run chronologically and the selected ordering applies
 * to the rows inside them instead. Non-date groupings order both levels.
 */
export function groupOrderingFor(grouping, ordering) {
  if (!isDateGrouping(grouping)) return ordering;
  return ordering === ORDER_DATE_ASC ? ORDER_DATE_ASC : ORDER_DATE_DESC;
}

// ── URL persistence ───────────────────────────────────────────
// Filters already round-trip through the URL, so a Display config should too:
// a grouped view can then be shared, bookmarked and survives a reload. Own
// params so the two systems stay independent — a URL carrying only filters
// keeps working and Display falls back to its defaults.
/** What the URL writes for "no column is hidden" — see `readFromUrl`. */
const HIDE_NONE = 'none';

const URL_KEYS = {
  view: 'view',
  grouping: 'group',
  subgrouping: 'subgroup',
  ordering: 'order',
  rowsPerGroup: 'rows',
  hiddenProperties: 'hide',
  showEmptyGroups: 'empty',
};

const DEFAULTS = {
  view: VIEW_LIST,
  // MI-336 — the page has always opened on a day-by-day list; until now the
  // menu called that "None" and left no way to ask for an actual flat list.
  // Naming it frees "None" to mean what it says.
  grouping: GROUPING_DAY,
  subgrouping: GROUPING_NONE,
  // Chronological, because the default view is a chronological list. The cost
  // is accepted: a category grouping now arrives ranked by recency rather than
  // by size, until the reader picks Amount there — after which it is
  // remembered.
  ordering: ORDER_DATE_DESC,
  rowsPerGroup: 10,
  hiddenProperties: PROPERTIES_HIDDEN_BY_DEFAULT,
  // Off by default: a month with no income is worth seeing, 300 empty days in
  // a year-long day grouping is not, and one switch serves both (rev 5).
  showEmptyGroups: false,
};

/**
 * Display settings survive navigation the same way filters do: through
 * browser storage, this tab's own first (`utils/tabStorage`, MI-326).
 *
 * The URL alone is not enough. Leaving Movements and coming back remounts the
 * page with a fresh URL, and FilterContext rebuilds the query string from its
 * own state — so a grouping that lived only in the URL is gone by the time
 * this hook reads it. The URL still wins when it carries settings, which is
 * what keeps a shared link meaning what its sender saw.
 */
const STORAGE_KEY = 'mikapital_display';

/**
 * Bumped when a *default* changes in a way a stored config cannot express.
 *
 * `hiddenProperties: []` means "everything on", and a config written before a
 * new column existed says exactly that — so replaying it turns on a column the
 * user never asked for. A config from before the bump gets the current
 * defaults folded in; one saved after it is obeyed.
 *
 * v3 covered the window in which `created` shipped next to a `date` column
 * that was then dropped.
 *
 * v4 (MI-336) is the same class of problem one level up. This hook writes its
 * *whole* state on mount, including values the user never touched, so every
 * reader who has ever opened Movements has `grouping: "none"` stored — which
 * meant "the default" when it was written and means "a flat list with no date
 * headers" now. Replaying it would hand the entire user base a view they never
 * asked for, so grouping and ordering are re-defaulted for anything written
 * before the bump.
 *
 * v5 (MI-336) turns the `date` column off wherever a date header already
 * carries it — Day being the default view, a stored config from v4 would keep
 * repeating the date on every row under the header that already says it.
 */
const STORAGE_VERSION = 5;

function readFromStorage() {
  if (typeof window === 'undefined') return null;
  try {
    // This tab's own settings, else the last used in any tab (MI-326).
    const raw = readTabState(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.v !== STORAGE_VERSION) {
      // Dropped rather than translated: the stored values are not wrong, they
      // are unreadable — nothing in them says whether the reader chose this
      // grouping or merely never touched the control. Falling through to
      // DEFAULTS is the only honest answer, and it costs one setting once.
      const { grouping, subgrouping, ordering, ...rest } = parsed;
      return {
        ...rest,
        hiddenProperties: [...new Set([
          ...(parsed.hiddenProperties || []),
          ...PROPERTIES_HIDDEN_BY_DEFAULT,
        ])],
      };
    }
    return parsed;
  } catch {
    return null;  // corrupt or unavailable storage is not worth failing over
  }
}

function writeToStorage(state) {
  if (typeof window === 'undefined') return;
  writeTabState(STORAGE_KEY, JSON.stringify({ ...state, v: STORAGE_VERSION }));
}

function readFromUrl() {
  if (typeof window === 'undefined') return { ...DEFAULTS };
  const p = new URLSearchParams(window.location.search);
  // A URL with no Display params at all falls back to what was last used,
  // rather than resetting a grouping the user never dismissed.
  const stored = p.has(URL_KEYS.grouping) || p.has(URL_KEYS.view)
    ? null
    : readFromStorage();
  if (stored) return { ...DEFAULTS, ...stored };
  const oneOf = (raw, allowed, fallback) => (allowed.includes(raw) ? raw : fallback);
  const rows = Number(p.get(URL_KEYS.rowsPerGroup));
  // `hide=none` is how a URL says "show every column", because the empty list
  // it means cannot be written as an empty value: `hide=` carries no
  // information, and reading it as an explicit choice is what pinned a stale
  // link to a column the reader never turned on — and re-saved it on arrival,
  // so clearing storage did not help either.
  const hideParam = p.get(URL_KEYS.hiddenProperties);
  const hidden = hideParam === HIDE_NONE
    ? []
    : hideParam
      ? hideParam.split(',').filter(prop => DISPLAY_PROPERTIES.includes(prop))
      : DEFAULTS.hiddenProperties;

  const grouping = oneOf(p.get(URL_KEYS.grouping), GROUPINGS, DEFAULTS.grouping);
  // The date column follows the grouping here too, not only when the reader
  // changes it from the menu — otherwise a link straight to `?group=none`
  // arrives at the one view with no date headers and no date column either.
  // An explicit `hide` in the URL is a choice and still wins.
  const withDateDefault = hideParam
    ? hidden
    : dateIsRedundantUnder(grouping)
      ? [...new Set([...hidden, 'date'])]
      : hidden.filter(prop => prop !== 'date');
  // Same for the goal mark under a goal grouping.
  const hiddenProperties = !hideParam && goalIsRedundantUnder(grouping)
    ? [...new Set([...withDateDefault, 'goal'])]
    : withDateDefault;

  return {
    view: oneOf(p.get(URL_KEYS.view), [VIEW_LIST, VIEW_BOARD], DEFAULTS.view),
    grouping,
    subgrouping: oneOf(p.get(URL_KEYS.subgrouping), SUBGROUPINGS, DEFAULTS.subgrouping),
    ordering: oneOf(p.get(URL_KEYS.ordering), ORDERINGS, DEFAULTS.ordering),
    rowsPerGroup: ROWS_PER_GROUP_OPTIONS.includes(rows) ? rows : DEFAULTS.rowsPerGroup,
    hiddenProperties,
    showEmptyGroups: p.get(URL_KEYS.showEmptyGroups) === 'true',
  };
}

function writeToUrl(state) {
  if (typeof window === 'undefined') return;
  const p = new URLSearchParams(window.location.search);
  Object.entries(URL_KEYS).forEach(([key, param]) => {
    const value = state[key];
    const sameSet = (a, b) =>
      a.length === b.length && a.every(v => b.includes(v));
    const isDefault = Array.isArray(value)
      ? sameSet(value, DEFAULTS[key] || [])
      : value === DEFAULTS[key];
    // Only non-defaults reach the URL, so an untouched Display leaves no trace
    // and a shared link stays about the filters.
    if (isDefault) p.delete(param);
    else if (Array.isArray(value)) p.set(param, value.length ? value.join(',') : HIDE_NONE);
    else p.set(param, String(value));
  });
  const qs = p.toString();
  window.history.replaceState(
    null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`
  );
}

export default function useDisplaySettings({ goalsEnabled = true } = {}) {
  const stored = useRef(readFromUrl()).current;
  if (!goalsEnabled && stored.grouping === GROUPING_GOAL) stored.grouping = DEFAULTS.grouping;
  if (!goalsEnabled && stored.subgrouping === GROUPING_GOAL) stored.subgrouping = GROUPING_NONE;
  // After the goal fallback, which can itself land a board on Day.
  const initial = fitToView(stored);
  const [view, setViewRaw] = useState(initial.view);
  const [grouping, setGroupingRaw] = useState(initial.grouping);
  const [subgrouping, setSubgroupingRaw] = useState(
    // A subgroup that matches its parent is a no-op; drop it rather than
    // rendering a level that groups everything into one child.
    subgroupingsFor(initial.grouping).includes(initial.subgrouping)
      && offeredIn(initial.view, initial.subgrouping)
      ? initial.subgrouping
      : GROUPING_NONE
  );
  const [ordering, setOrdering] = useState(initial.ordering);
  const [rowsPerGroup, setRowsPerGroup] = useState(initial.rowsPerGroup);
  const [hiddenProperties, setHiddenProperties] = useState(initial.hiddenProperties);
  const [showEmptyGroups, setShowEmptyGroups] = useState(initial.showEmptyGroups);

  useEffect(() => {
    const state = {
      view, grouping, subgrouping, ordering, rowsPerGroup, hiddenProperties,
      showEmptyGroups,
    };
    writeToUrl(state);
    writeToStorage(state);
  }, [view, grouping, subgrouping, ordering, rowsPerGroup, hiddenProperties,
      showEmptyGroups]);

  const toggleProperty = useCallback((prop) => {
    setHiddenProperties(prev =>
      prev.includes(prop) ? prev.filter(p => p !== prop) : [...prev, prop]
    );
  }, []);

  const isVisible = useCallback(
    (prop) => !hiddenProperties.includes(prop),
    [hiddenProperties]
  );

  /**
   * Back to the view the page opens on. Worth an explicit action: the settings
   * survive reloads, so a reader who has nested two groupings and hidden four
   * columns has no other way back.
   */
  const reset = useCallback(() => {
    setViewRaw(DEFAULTS.view);
    setGroupingRaw(DEFAULTS.grouping);
    setSubgroupingRaw(DEFAULTS.subgrouping);
    setOrdering(DEFAULTS.ordering);
    setRowsPerGroup(DEFAULTS.rowsPerGroup);
    setHiddenProperties(DEFAULTS.hiddenProperties);
    setShowEmptyGroups(DEFAULTS.showEmptyGroups);
  }, []);

  const isDefaultDisplay = view === DEFAULTS.view
    && grouping === DEFAULTS.grouping
    && subgrouping === DEFAULTS.subgrouping
    && ordering === DEFAULTS.ordering
    && rowsPerGroup === DEFAULTS.rowsPerGroup
    && showEmptyGroups === DEFAULTS.showEmptyGroups
    && hiddenProperties.length === DEFAULTS.hiddenProperties.length
    && hiddenProperties.every(prop => DEFAULTS.hiddenProperties.includes(prop));

  /**
   * Changing grouping can strand the active ordering (name on a date bucket).
   * Fall back to the most recent date rather than sending the backend an
   * ordering the UI is about to grey out.
   */
  const setGrouping = useCallback((next) => {
    setGroupingRaw(next);
    // Drop a subgroup the new grouping no longer admits (day → month, say).
    setSubgroupingRaw(prev =>
      subgroupingsFor(next).includes(prev) ? prev : GROUPING_NONE
    );
    setOrdering(prev =>
      prev === ORDER_NAME_ASC && isDateGrouping(next) ? ORDER_DATE_DESC : prev
    );
    // Same shape as the two fallbacks above: the new grouping decides.
    setHiddenProperties(prev => propertiesFor(next, prev));
  }, []);

  const setSubgrouping = useCallback((next) => {
    setSubgroupingRaw(subgroupingsFor(grouping).includes(next) ? next : GROUPING_NONE);
  }, [grouping]);

  /**
   * MI-326 — the board takes Day away, so switching to it moves a day
   * grouping to the board's own default and drops a day subgroup. Going back
   * to the list keeps whatever the board left: nothing is restored behind the
   * reader's back.
   */
  const setView = useCallback((next) => {
    setViewRaw(next);
    if (!offeredIn(next, grouping)) setGrouping(BOARD_GROUPING);
    else if (!offeredIn(next, subgrouping)) setSubgroupingRaw(GROUPING_NONE);
  }, [grouping, subgrouping, setGrouping]);

  /**
   * Called when the visible currency count changes; same fallback rule as the
   * menu's gate — and, since rev 5, the same escape hatch. Stepping a user out
   * of amount ordering while they are subgrouped by currency would undo the
   * choice they just made to earn it.
   */
  const reconcileOrdering = useCallback((currencyCount, { converting = false } = {}) => {
    if (converting || splitsByCurrency(grouping, subgrouping)) return;
    setOrdering(prev =>
      currencyCount > 1 && (prev === ORDER_AMOUNT_DESC || prev === ORDER_AMOUNT_ASC)
        ? ORDER_DATE_DESC
        : prev
    );
  }, [grouping, subgrouping]);

  const isGrouped = grouping !== GROUPING_NONE;

  const hasSubgroup = isGrouped && subgrouping !== GROUPING_NONE;

  return useMemo(() => ({
    view, setView,
    grouping, setGrouping,
    subgrouping, setSubgrouping,
    ordering, setOrdering,
    rowsPerGroup, setRowsPerGroup,
    hiddenProperties, toggleProperty, isVisible,
    showEmptyGroups, setShowEmptyGroups,
    reconcileOrdering,
    reset, isDefaultDisplay,
    isGrouped, hasSubgroup,
  }), [view, grouping, subgrouping, ordering, rowsPerGroup, hiddenProperties,
       showEmptyGroups, setView, setGrouping, setSubgrouping, toggleProperty, isVisible,
       reconcileOrdering, reset, isDefaultDisplay, isGrouped, hasSubgroup]);
}
